import { Fragment, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users,
  Search,
  X,
  Check,
  AlertTriangle,
  QrCode,
  Clock,
  BookOpen,
  GraduationCap,
  UserPlus,
  Heart,
  CalendarClock,
  SlidersHorizontal,
} from 'lucide-react';
import { Button, Card, CardContent, Input, Alert, AlertDescription, cn } from '@jeraisonne/ui';
import { SUBJECTS } from '@jeraisonne/db';
import type { SchoolLevel, SubjectSlug } from '@jeraisonne/db';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { useSearchTutors, useRequestTutor } from '@/lib/student/search-tutors';
import { useUpdateTutorLinkSubjects } from '@/lib/tutor/mutations';
import { useStudentProfile } from '@/hooks/use-student-profile';
import { useContactLayout, type StudentSectionId } from '@/lib/contact-layout';
import { filterByQuery, hasSearchQuery } from '@/lib/contact-search';
import { useMyPeers } from '@/lib/student/peers';
import { PeerRequestsSection } from '@/components/contacts/peer-requests-section';
import { useFollowedTeachers } from '@/lib/student/teacher-follows';
import { AvatarCircle } from '@/components/avatar-circle';
import { SubjectsMultiselect } from '@/components/subjects-multiselect';
import { PeerInviteCard } from '@/components/contacts/peer-invite-card';
import { PeersSection } from '@/components/contacts/peers-section';
import { ParentsSection, useMyParents } from '@/components/contacts/parents-section';
import { ContactSearchInput } from '@/components/contacts/contact-search-input';
import { FilesShortcut } from '@/components/documents/files-shortcut';
import { DiscussionsShortcut } from '@/features/discussions/discussions-shortcut';
import { ParentLinkCard } from '@/components/contacts/parent-link-card';
import { FollowedTeachersSection } from '@/components/contacts/followed-teachers-section';
import { JoinClassCard } from '@/components/contacts/join-class-card';
import { MyConnectQr } from '@/components/contacts/my-connect-qr';
import { WhoSeesWhat } from '@/components/contacts/who-sees-what';
import {
  ContactLayoutButton,
  ContactLayoutPanel,
} from '@/components/contacts/contact-layout-settings';

// ─── Types ────────────────────────────────────────────────────────────────────

type TutorContact = {
  id: string;
  status: string;
  initiated_by: 'tutor' | 'student';
  invite_code: string | null;
  /** Matière(s) couvertes par ce lien (migration 0227) — vide = toutes matières. */
  subjects: SubjectSlug[];
  tutor: {
    id: string;
    display_name: string;
    avatar_url: string | null;
  } | null;
};

// ─── Hooks ────────────────────────────────────────────────────────────────────

function useMyTutors(studentId: string) {
  return useQuery({
    queryKey: ['my-tutors', studentId],
    enabled: !!studentId,
    staleTime: 30_000,
    queryFn: async (): Promise<TutorContact[]> => {
      const { data, error } = await supabase
        .from('tutor_links')
        .select(
          `id, status, initiated_by, invite_code, subjects,
           profiles!tutor_links_tutor_id_fkey ( id, display_name:visible_name, avatar_url )`,
        )
        .eq('student_id', studentId)
        .in('status', ['active', 'pending'])
        .order('created_at', { ascending: false });
      if (error) throw error;
      type RawRow = {
        id: string;
        status: string;
        initiated_by: 'tutor' | 'student';
        invite_code: string | null;
        subjects: SubjectSlug[];
        profiles: TutorContact['tutor'];
      };
      return ((data ?? []) as unknown as RawRow[]).map((row) => ({
        id: row.id,
        status: row.status,
        initiated_by: row.initiated_by,
        invite_code: row.invite_code,
        subjects: row.subjects ?? [],
        tutor: row.profiles,
      }));
    },
  });
}

function useAcceptInviteCode(studentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (code: string) => {
      const { error } = await supabase.rpc('accept_tutor_invite', { p_code: code });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-tutors', studentId] });
    },
  });
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function TutorInviteCodeForm({ studentId }: { studentId: string }) {
  const [code, setCode] = useState('');
  const [success, setSuccess] = useState(false);
  const accept = useAcceptInviteCode(studentId);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) return;
    accept.mutate(trimmed, {
      onSuccess: () => {
        setSuccess(true);
        setCode('');
        setTimeout(() => setSuccess(false), 4000);
      },
    });
  }

  return (
    <Card className="border-ink-200">
      <CardContent className="space-y-3 py-4">
        <div>
          <p className="text-ink-900 text-sm font-semibold">Accepter une invitation prof</p>
          <p className="text-ink-500 mt-0.5 text-xs">
            Saisis le code que ton prof t'a partagé pour valider le lien.
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-2">
          <div className="flex gap-2">
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Code d'invitation prof"
              className="font-mono uppercase tracking-widest"
              disabled={accept.isPending}
              autoCapitalize="characters"
              maxLength={20}
            />
            <Button type="submit" disabled={!code.trim() || accept.isPending} size="sm">
              {accept.isPending ? '…' : <Check className="h-4 w-4" />}
            </Button>
          </div>
          {accept.isError && (
            <p className="text-accent-600 text-xs">{(accept.error as Error).message}</p>
          )}
          {success && <p className="text-xs font-medium text-emerald-600">Invitation acceptée !</p>}
        </form>
      </CardContent>
    </Card>
  );
}

function TutorSearchPanel({ studentId, isMinor }: { studentId: string; isMinor: boolean }) {
  const [query, setQuery] = useState('');
  const [subjects, setSubjects] = useState<SubjectSlug[]>([]);
  const [sentIds, setSentIds] = useState<Set<string>>(new Set());
  const [errorMap, setErrorMap] = useState<Record<string, string>>({});
  const { data: results = [], isLoading } = useSearchTutors(query);
  const studentProfile = useStudentProfile(studentId);
  const requestTutor = useRequestTutor(studentId);

  function handleRequest(tutorId: string) {
    requestTutor.mutate(
      { tutorId, subjects },
      {
        onSuccess: () => setSentIds((s) => new Set([...s, tutorId])),
        onError: (e) => setErrorMap((m) => ({ ...m, [tutorId]: (e as Error).message })),
      },
    );
  }

  return (
    <Card className="border-ink-200">
      <CardContent className="space-y-3 py-4">
        <p className="text-ink-900 text-sm font-semibold">Rejoindre un tuteur</p>
        {isMinor && (
          <Alert variant="warning">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="text-xs">
              En tant que mineur, l'ajout d'un contact professeur est soumis à validation. Tes
              parents ou tuteur légal seront informés si nécessaire.
            </AlertDescription>
          </Alert>
        )}
        <div>
          <span className="text-ink-600 mb-1.5 block text-xs font-semibold">
            Pour quelle(s) matière(s) ? (optionnel)
          </span>
          <SubjectsMultiselect
            value={subjects}
            onChange={setSubjects}
            levelFilter={studentProfile.data?.level}
          />
        </div>
        <div className="relative">
          <Search className="text-ink-400 absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Recherche un professeur par nom…"
            className="pl-9"
          />
          {query && (
            <button
              type="button"
              className="text-ink-400 absolute right-3 top-1/2 -translate-y-1/2"
              onClick={() => setQuery('')}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {query.trim().length >= 2 && (
          <div className="space-y-2">
            {isLoading ? (
              <p className="text-ink-400 text-sm">Recherche…</p>
            ) : results.length === 0 ? (
              <p className="text-ink-400 text-sm">Aucun résultat pour «&nbsp;{query}&nbsp;».</p>
            ) : (
              results.map((tutor) => (
                <div
                  key={tutor.id}
                  className="border-ink-100 flex items-center gap-3 rounded-xl border bg-white p-3"
                >
                  <Link
                    to="/tutors/$tutorId"
                    params={{ tutorId: tutor.id }}
                    className="flex min-w-0 flex-1 items-center gap-3"
                  >
                    <AvatarCircle name={tutor.display_name} src={tutor.avatar_url} size="sm" />
                    <div className="min-w-0 flex-1">
                      <p className="text-ink-900 truncate text-sm font-semibold hover:underline">
                        {tutor.display_name}
                      </p>
                      {errorMap[tutor.id] && (
                        <p className="text-accent-600 text-xs">{errorMap[tutor.id]}</p>
                      )}
                    </div>
                  </Link>
                  <Button
                    size="sm"
                    variant={sentIds.has(tutor.id) ? 'outline' : 'primary'}
                    disabled={sentIds.has(tutor.id) || requestTutor.isPending}
                    onClick={() => handleRequest(tutor.id)}
                  >
                    {sentIds.has(tutor.id) ? 'Demande envoyée' : 'Demander'}
                  </Button>
                </div>
              ))
            )}
          </div>
        )}

        {query.trim().length > 0 && query.trim().length < 2 && (
          <p className="text-ink-400 text-xs">Saisis au moins 2 caractères pour rechercher.</p>
        )}
      </CardContent>
    </Card>
  );
}

function TutorLinkSubjectsEditor({
  linkId,
  studentId,
  subjects,
  level,
}: {
  linkId: string;
  studentId: string;
  subjects: SubjectSlug[];
  level: SchoolLevel | null;
}) {
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState<SubjectSlug[]>(subjects);
  const update = useUpdateTutorLinkSubjects([['my-tutors', studentId]]);

  if (!editing) {
    return (
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        <span className="text-ink-500 text-xs">
          {subjects.length === 0
            ? 'Toutes matières'
            : subjects.map((s) => SUBJECTS.find((sub) => sub.value === s)?.label ?? s).join(', ')}
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setSelected(subjects);
            setEditing(true);
          }}
          className="text-primary-600 text-xs font-medium underline"
        >
          Modifier
        </button>
      </div>
    );
  }

  return (
    <div
      className="mt-2 space-y-2 rounded-xl bg-white/60 p-2"
      onClick={(e) => e.stopPropagation()}
      role="presentation"
    >
      <SubjectsMultiselect value={selected} onChange={setSelected} levelFilter={level} />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(false)}>
          Annuler
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={update.isPending}
          onClick={() =>
            update.mutate({ linkId, subjects: selected }, { onSuccess: () => setEditing(false) })
          }
        >
          {update.isPending ? '…' : 'Confirmer'}
        </Button>
      </div>
    </div>
  );
}

/** Textes dans lesquels la recherche cherche pour un professeur (nom, matières du lien). */
function tutorSearchFields(contact: TutorContact): string[] {
  return [
    contact.tutor?.display_name ?? '',
    contact.subjects.length === 0
      ? 'Toutes matières'
      : contact.subjects.map((s) => SUBJECTS.find((sub) => sub.value === s)?.label ?? s).join(' '),
  ];
}

// ─── Page principale ──────────────────────────────────────────────────────────

type ActionTab = 'qr' | 'peer' | 'parent' | 'class' | 'tutor';

export function StudentContactsPage() {
  const { user } = useAuth();
  const studentId = user?.id ?? '';
  const [tab, setTab] = useState<ActionTab | null>(null);

  const { data: myTutors = [], isLoading } = useMyTutors(studentId);
  const studentProfile = useStudentProfile(studentId);
  const studentLevel: SchoolLevel | null = studentProfile.data?.level ?? null;

  const { data: isMinor = false } = useQuery({
    queryKey: ['student-is-minor', studentId],
    enabled: !!studentId,
    staleTime: 1000 * 60 * 10,
    queryFn: async () => {
      const { data } = await supabase
        .from('student_profiles')
        .select('parental_consent_required')
        .eq('id', studentId)
        .maybeSingle();
      return data?.parental_consent_required ?? false;
    },
  });

  const activeContacts = myTutors.filter((t) => t.status === 'active');
  const pendingContacts = myTutors.filter((t) => t.status === 'pending');
  const hasActiveTutor = activeContacts.length > 0;

  // Listes affichées + ordre, choisis par l'élève (migration 0251). Tant qu'il n'a rien
  // enregistré, l'ordre historique est conservé : « Mes professeurs » en tête s'il y en
  // a au moins un, sinon son bloc vide passe en bas (après parents, camarades, profs suivis).
  const layout = useContactLayout(
    'student',
    hasActiveTutor
      ? ['tutors', 'parents', 'peers', 'followed']
      : ['parents', 'peers', 'followed', 'tutors'],
  );
  const [customizeOpen, setCustomizeOpen] = useState(false);

  // Recherche dans les contacts : filtre local de toutes les listes affichées.
  const [query, setQuery] = useState('');
  const searching = hasSearchQuery(query);
  const shownActive = filterByQuery(activeContacts, query, tutorSearchFields);
  const shownPending = filterByQuery(pendingContacts, query, tutorSearchFields);
  // Parents, camarades et profs suivis filtrent eux-mêmes leur affichage (prop `filter`) ;
  // la page relit leurs données (mêmes clés React Query, aucune requête en plus) pour
  // savoir si AU MOINS une liste affichée a un résultat.
  const { data: parents = [] } = useMyParents(studentId);
  const { data: peers = [] } = useMyPeers(studentId);
  const { data: followed = [] } = useFollowedTeachers(studentId);

  function toggle(next: ActionTab) {
    setTab((cur) => (cur === next ? null : next));
  }

  // Pendant une recherche, une liste sans résultat disparaît (pas de faux « Aucun professeur lié »).
  const activeTutorsSection =
    searching && shownActive.length === 0 ? null : (
      <section className="space-y-3">
        <p className="text-ink-400 text-xs font-semibold uppercase tracking-wider">
          Mes professeurs ({shownActive.length})
        </p>
        {isLoading ? (
          <ListSkeleton />
        ) : activeContacts.length === 0 ? (
          <Card className="border-ink-100 border-dashed">
            <CardContent className="py-6 text-center">
              <Users className="text-ink-300 mx-auto mb-2 h-8 w-8" />
              <p className="text-ink-500 text-sm font-medium">Aucun professeur lié</p>
              <p className="text-ink-400 mt-1 text-xs">
                Cherche ton professeur ou entre un code d'invitation.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {shownActive.map((contact) => {
              const tutor = contact.tutor;
              const inner = (
                <CardContent className="flex items-center gap-3 py-3">
                  <AvatarCircle
                    name={tutor?.display_name ?? 'Prof'}
                    src={tutor?.avatar_url ?? null}
                    size="sm"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-ink-900 truncate text-sm font-semibold hover:underline">
                      {tutor?.display_name ?? 'Professeur'}
                    </p>
                    <p className="text-xs font-medium text-emerald-600">Lié ✓</p>
                  </div>
                </CardContent>
              );
              return (
                <Card key={contact.id} className="border-ink-100 transition-colors">
                  {tutor?.id ? (
                    <Link
                      to="/tutors/$tutorId"
                      params={{ tutorId: tutor.id }}
                      className="hover:bg-ink-50/40 block active:scale-[0.99]"
                    >
                      {inner}
                    </Link>
                  ) : (
                    inner
                  )}
                  <div className="px-4 pb-3">
                    <TutorLinkSubjectsEditor
                      linkId={contact.id}
                      studentId={studentId}
                      subjects={contact.subjects}
                      level={studentLevel}
                    />
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </section>
    );

  // Le raccourci n'a pas sa place dans des résultats de recherche.
  const bookCourseSection = hasActiveTutor && !searching && (
    <Link
      to="/student/rendez-vous"
      className="border-primary-100 bg-primary-50 hover:bg-primary-100/70 flex items-center gap-3 rounded-2xl border p-4 transition-colors active:scale-[0.99]"
    >
      <div className="bg-primary-500 flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
        <CalendarClock className="h-5 w-5 text-white" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-ink-900 text-sm font-semibold">Prendre rendez-vous</p>
        <p className="text-ink-500 mt-0.5 text-xs">Voir les créneaux disponibles</p>
      </div>
    </Link>
  );

  const pendingTutorsSection = shownPending.length > 0 && (
    <section className="space-y-3">
      <p className="text-ink-400 text-xs font-semibold uppercase tracking-wider">
        Demandes en attente ({shownPending.length})
      </p>
      <div className="space-y-2">
        {shownPending.map((contact) => {
          const tutor = contact.tutor;
          const inner = (
            <CardContent className="flex items-center gap-3 py-3">
              <AvatarCircle
                name={tutor?.display_name ?? 'Prof'}
                src={tutor?.avatar_url ?? null}
                size="sm"
              />
              <div className="min-w-0 flex-1">
                <p className="text-ink-900 truncate text-sm font-semibold hover:underline">
                  {tutor?.display_name ?? 'Professeur'}
                </p>
                <div className="flex items-center gap-1 text-amber-600">
                  <Clock className="h-3 w-3" />
                  <p className="text-xs font-medium">
                    {contact.initiated_by === 'student'
                      ? 'En attente que le prof accepte'
                      : 'En attente de ton acceptation'}
                  </p>
                </div>
                <p className="text-ink-400 mt-0.5 text-xs">
                  {contact.subjects.length === 0
                    ? 'Toutes matières'
                    : contact.subjects
                        .map((s) => SUBJECTS.find((sub) => sub.value === s)?.label ?? s)
                        .join(', ')}
                </p>
              </div>
            </CardContent>
          );
          return (
            <Card
              key={contact.id}
              className="border-amber-200 bg-amber-50/50 transition-colors hover:bg-amber-50"
            >
              {tutor?.id ? (
                <Link to="/tutors/$tutorId" params={{ tutorId: tutor.id }}>
                  {inner}
                </Link>
              ) : (
                inner
              )}
            </Card>
          );
        })}
      </div>
    </section>
  );

  // « Mes professeurs » regroupe la liste, le raccourci « Prendre rendez-vous » et les
  // demandes en attente : tous liés aux professeurs, masqués/déplacés ensemble.
  const sectionNodes: Record<StudentSectionId, ReactNode> = {
    tutors: (
      <>
        {activeTutorsSection}
        {bookCourseSection}
        {pendingTutorsSection}
      </>
    ),
    // Mes parents — visible dès qu'il y a au moins un parent lié (la section se masque seule sinon)
    parents: <ParentsSection studentId={studentId} filter={query} />,
    peers: <PeersSection studentId={studentId} onAddPeer={() => setTab('peer')} filter={query} />,
    followed: <FollowedTeachersSection studentId={studentId} filter={query} />,
  };

  // Au moins une liste AFFICHÉE (non masquée) a un résultat ?
  const matchCounts: Record<StudentSectionId, number> = {
    tutors: shownActive.length + shownPending.length,
    parents: filterByQuery(parents, query, (row) => [row.parent?.display_name]).length,
    peers: filterByQuery(peers, query, (peer) => [peer.display_name]).length,
    followed: filterByQuery(followed, query, (teacher) => [teacher.display_name]).length,
  };
  const anyMatch = layout.visibleIds.some((id) => matchCounts[id as StudentSectionId] > 0);

  return (
    <div className="pb-safe mx-auto max-w-lg space-y-6 px-4 py-6 sm:py-8">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Users className="text-primary-600 h-6 w-6" />
          <h1 className="text-ink-900 text-2xl font-bold">Mon réseau</h1>
        </div>
        <ContactLayoutButton open={customizeOpen} onClick={() => setCustomizeOpen((v) => !v)} />
      </div>

      <ContactSearchInput value={query} onChange={setQuery} />

      {customizeOpen && (
        <ContactLayoutPanel
          sections={layout.sections}
          isCustomized={layout.isCustomized}
          disabled={layout.isSaving}
          onToggle={layout.toggle}
          onMove={layout.move}
          onReset={layout.reset}
        />
      )}

      {/* Demandes de camarades reçues (ex. défi SKILLCARD relevé) — à traiter. */}
      <PeerRequestsSection studentId={studentId} filter={query} />

      {/* Se relier : toutes les façons visibles d'un coup d'œil (plus de rangée qui défile
          et cache la moitié des actions). Le QR code relie à n'importe qui en un scan.
          Pendant une recherche, masqué… sauf si personne n'est trouvé (on propose d'ajouter). */}
      {(!searching || !anyMatch) && (
        <section aria-labelledby="connect-title" className="space-y-2">
          <h2
            id="connect-title"
            className="text-ink-400 text-xs font-semibold uppercase tracking-wider"
          >
            Se relier
          </h2>
          <div className="grid grid-cols-2 gap-2">
            <ActionButton
              icon={<QrCode className="h-4 w-4" />}
              label="Mon QR code"
              hint="Un scan, et c’est fait"
              active={tab === 'qr'}
              onClick={() => toggle('qr')}
              wide
            />
            <ActionButton
              icon={<UserPlus className="h-4 w-4" />}
              label="Un camarade"
              active={tab === 'peer'}
              onClick={() => toggle('peer')}
            />
            <ActionButton
              icon={<GraduationCap className="h-4 w-4" />}
              label="Un prof"
              active={tab === 'tutor'}
              onClick={() => toggle('tutor')}
            />
            <ActionButton
              icon={<Heart className="h-4 w-4" />}
              label="Un parent"
              active={tab === 'parent'}
              onClick={() => toggle('parent')}
            />
            <ActionButton
              icon={<BookOpen className="h-4 w-4" />}
              label="Ma classe"
              active={tab === 'class'}
              onClick={() => toggle('class')}
            />
          </div>

          {tab === 'qr' && (
            <Card className="border-primary-200 bg-primary-50/40">
              <CardContent className="py-4">
                <MyConnectQr userId={studentId} />
              </CardContent>
            </Card>
          )}
          {tab === 'peer' && <PeerInviteCard studentId={studentId} />}
          {tab === 'parent' && <ParentLinkCard userId={studentId} audience="student" />}
          {tab === 'class' && <JoinClassCard studentId={studentId} />}
          {tab === 'tutor' && (
            <div className="space-y-3">
              <TutorSearchPanel studentId={studentId} isMinor={isMinor} />
              <TutorInviteCodeForm studentId={studentId} />
            </div>
          )}
        </section>
      )}

      {/* Listes de contacts — ordre et visibilité choisis par l'élève (migration 0251) */}
      {layout.isLoading ? (
        <ListSkeleton />
      ) : layout.visibleIds.length === 0 ? (
        <Card className="border-ink-100 border-dashed">
          <CardContent className="py-6 text-center">
            <SlidersHorizontal className="text-ink-300 mx-auto mb-2 h-8 w-8" />
            <p className="text-ink-500 text-sm font-medium">Aucune liste affichée</p>
            <p className="text-ink-400 mt-1 text-xs">
              Active au moins une liste avec le bouton « Personnaliser mes listes ».
            </p>
          </CardContent>
        </Card>
      ) : searching && !anyMatch ? (
        <Card className="border-ink-100 border-dashed">
          <CardContent className="py-6 text-center">
            <Search className="text-ink-300 mx-auto mb-2 h-8 w-8" />
            <p className="text-ink-500 text-sm font-medium">
              Aucun résultat pour « {query.trim()} »
            </p>
            <p className="text-ink-400 mt-1 text-xs">
              Vérifie l'orthographe, ou efface la recherche pour revoir tous tes contacts.
            </p>
          </CardContent>
        </Card>
      ) : (
        layout.visibleIds.map((id) => (
          <Fragment key={id}>{sectionNodes[id as StudentSectionId]}</Fragment>
        ))
      )}

      {/* Secondaire : qui voit quoi, puis les raccourcis (absents des barres mobiles). */}
      {!searching && <WhoSeesWhat studentId={studentId} />}
      {!searching && <FilesShortcut to="/student/files" />}
      {!searching && <DiscussionsShortcut to="/student/discussions" />}
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className="space-y-2">
      {[1, 2].map((i) => (
        <Card key={i} className="border-ink-100 animate-pulse">
          <CardContent className="flex items-center gap-3 py-3">
            <div className="bg-ink-100 h-10 w-10 rounded-full" />
            <div className="bg-ink-100 h-4 w-32 rounded" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ActionButton({
  icon,
  label,
  hint,
  active,
  onClick,
  wide = false,
}: {
  icon: React.ReactNode;
  label: string;
  hint?: string;
  active: boolean;
  onClick: () => void;
  wide?: boolean;
}) {
  return (
    <button
      type="button"
      aria-expanded={active}
      onClick={onClick}
      className={cn(
        'font-display flex min-h-[2.75rem] items-center gap-2 rounded-xl border px-3.5 py-2 text-left text-sm font-semibold transition-all active:scale-[.98]',
        wide && 'col-span-2',
        active
          ? 'bg-primary-500 border-primary-500 text-white shadow-md'
          : 'border-ink-200 text-ink-700 hover:border-primary-300 hover:text-primary-700 ocean:bg-white/5 bg-white',
      )}
    >
      <span className="shrink-0">{icon}</span>
      <span className="min-w-0">
        <span className="block">{label}</span>
        {hint && (
          <span
            className={cn(
              'block font-sans text-xs font-normal',
              active ? 'text-white/80' : 'text-ink-500',
            )}
          >
            {hint}
          </span>
        )}
      </span>
    </button>
  );
}
